import type { StepStatus, SyncProgress } from "../../services/order-sync"

export type SyncStat = { label: string; count: number; className: string }
export type SyncStep = {
  label: string
  status: StepStatus
  stats: SyncStat[]
  notes: string[]
  hasIssues: boolean
}

const stat = (label: string, count: number, className = "text-muted-foreground"): SyncStat => ({
  label,
  count,
  className,
})

export function buildSyncSteps(progress: SyncProgress): SyncStep[] {
  return [
    { label: "Marketplaces", status: progress.marketplaces },
    { label: "Baselinker", status: progress.baselinker },
    { label: "Magento", status: progress.magento },
    { label: "OMS", status: progress.oms },
    { label: "Detect", status: progress.detect },
  ].map((step) => ({ ...step, ...stepResult(step.status) }))
}

function stepResult(status: StepStatus): Pick<SyncStep, "stats" | "notes" | "hasIssues"> {
  if (status.kind === "error") {
    return {
      stats: [stat("failed", 1, "text-destructive")],
      notes: [status.message],
      hasIssues: true,
    }
  }
  if (status.kind !== "done") {
    return { stats: [], notes: [], hasIssues: status.kind === "skipped" }
  }
  const result = status.result
  if (result.kind === "marketplaces") {
    const totals = result.summary.results.reduce(
      (sum, item) =>
        item.ok
          ? {
              inserted: sum.inserted + item.inserted,
              updated: sum.updated + item.updated,
              failed: sum.failed,
            }
          : { ...sum, failed: sum.failed + 1 },
      { inserted: 0, updated: 0, failed: 0 }
    )
    return {
      stats: [
        stat("new", totals.inserted, "text-foreground"),
        stat("updated", totals.updated),
        stat("failed", totals.failed, "text-destructive"),
      ].filter((item) => item.count > 0),
      notes: result.summary.results.flatMap((item) =>
        item.ok ? [] : [`${item.marketplace}: ${item.error}`]
      ),
      hasIssues: totals.failed > 0,
    }
  }
  if (result.kind === "detect") {
    return {
      stats: [
        stat("flagged", result.summary.flagged, "text-warning"),
        stat("cleared", result.summary.cleared, "text-success"),
      ].filter((item) => item.count > 0),
      notes: [],
      hasIssues: false,
    }
  }
  const enrich = result.summary
  return {
    stats: [
      stat("linked", enrich.linked, "text-foreground"),
      stat("not found", enrich.notFound),
      stat("ambiguous", enrich.ambiguous, "text-warning"),
      stat("errors", enrich.errors, "text-destructive"),
      stat("no email", enrich.skippedNoEmail),
    ].filter((item) => item.count > 0),
    notes: [],
    hasIssues: enrich.errors > 0 || enrich.ambiguous > 0,
  }
}

export function syncTotals(steps: SyncStep[]) {
  const sum = (label: string) =>
    steps
      .flatMap((step) => step.stats)
      .filter((item) => item.label === label)
      .reduce((total, item) => total + item.count, 0)
  return {
    counts: [
      stat("new", sum("new")),
      stat("updated", sum("updated")),
      stat("flagged", sum("flagged")),
    ].filter((item) => item.count > 0),
    issues: [
      stat("failed", sum("failed")),
      stat("errors", sum("errors")),
      stat("ambiguous", sum("ambiguous")),
      stat("skipped", steps.filter((step) => step.status.kind === "skipped").length),
    ].filter((item) => item.count > 0),
  }
}
